import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, ArrowUpRight, List } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import api from "@/lib/api";
import { LocationDialog } from "@/components/LocationDialog";
import { Button } from "@/components/ui/button";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Imperatively recenter the leaflet map without forcing a full remount —
// preserves user pan/zoom state on every navigation back to /map.
const MapRecenter = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    map.setView(center, zoom, { animate: true });
  }, [map, center[0], center[1]]); // intentionally NOT on zoom — let user zoom freely
  return null;
};

// Custom SVG pin in our brand sage with a hot amber for boosted/featured
const buildPin = (color) =>
  L.divIcon({
    className: "",
    iconSize: [30, 38],
    iconAnchor: [15, 38],
    popupAnchor: [0, -32],
    html: `
      <svg viewBox="0 0 32 40" width="30" height="38" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="ds" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.55"/>
          </filter>
        </defs>
        <path filter="url(#ds)" fill="${color}" stroke="rgba(0,0,0,0.4)" stroke-width="1"
              d="M16 1c8 0 14 6 14 14 0 10-14 24-14 24S2 25 2 15C2 7 8 1 16 1Z"/>
        <circle cx="16" cy="15" r="5.5" fill="rgba(15,17,15,0.85)"/>
      </svg>`,
  });

const PIN_SAGE = buildPin("#7FA28A");
const PIN_AMBER = buildPin("#D4A268");
const PIN_USER = buildPin("#5CA9FF");

// Center of PA as default
const DEFAULT_CENTER = [40.3, -77.0];
const DEFAULT_ZOOM = 7;

export default function MapPage() {
  const { location } = useApp();
  const [dispensaries, setDispensaries] = useState([]);
  const [locOpen, setLocOpen] = useState(false);

  useEffect(() => {
    api.get("/dispensaries").then((r) => setDispensaries(r.data));
  }, []);

  const center = useMemo(
    () => (location ? [location.lat, location.lon] : DEFAULT_CENTER),
    [location]
  );
  const zoom = location ? 10 : DEFAULT_ZOOM;

  return (
    <main className="relative">
      <div className="container-narrow pt-10 pb-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow mb-2 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-accent" />
              Find dispensaries
            </div>
            <h1 className="font-serif text-4xl">Map of Pennsylvania</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {dispensaries.length} licensed shops · drop a pin and tap &ldquo;Visit
              menu&rdquo;.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="map-set-location"
              onClick={() => setLocOpen(true)}
              variant="outline"
              className="rounded-full bg-transparent border-border"
            >
              <MapPin className="w-3.5 h-3.5 mr-1.5" />
              {location
                ? `${location.label || location.city || "PA"}`
                : "Set my location"}
            </Button>
            <Button asChild variant="ghost" className="rounded-full">
              <Link to="/dispensaries" data-testid="map-to-list">
                <List className="w-3.5 h-3.5 mr-1.5" /> List view
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div
        className="container-narrow rounded-3xl overflow-hidden border border-border bg-card relative"
        style={{ height: "70vh", minHeight: 480 }}
        data-testid="map-container"
      >
        <MapContainer
          center={center}
          zoom={zoom}
          scrollWheelZoom
          style={{ height: "100%", width: "100%", background: "#1A1D1B" }}
        >
          <MapRecenter center={center} zoom={zoom} />
          <TileLayer
            attribution={
              MAPBOX_TOKEN
                ? '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            }
            url={
              MAPBOX_TOKEN
                ? `https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`
                : "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            }
            tileSize={MAPBOX_TOKEN ? 512 : 256}
            zoomOffset={MAPBOX_TOKEN ? -1 : 0}
          />

          {location && (
            <>
              <Marker position={[location.lat, location.lon]} icon={PIN_USER}>
                <Popup>
                  <div className="font-serif text-base">You</div>
                  <div className="text-xs">
                    {location.label || location.city}{" "}
                    {location.zip ? `· ${location.zip}` : ""}
                  </div>
                </Popup>
              </Marker>
              <Circle
                center={[location.lat, location.lon]}
                radius={16093}
                pathOptions={{
                  color: "#5CA9FF",
                  fillColor: "#5CA9FF",
                  fillOpacity: 0.06,
                  weight: 1,
                  dashArray: "4 6",
                }}
              />
            </>
          )}

          {dispensaries.map((d) => (
            <Marker
              key={d.slug}
              position={[d.lat, d.lon]}
              icon={d.featured ? PIN_AMBER : PIN_SAGE}
            >
              <Popup>
                <div className="space-y-1 min-w-[180px]">
                  <div className="font-serif text-base leading-tight">
                    {d.name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {d.city}, {d.state}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {d.address}
                  </div>
                  <div className="flex gap-1 mt-1.5">
                    {d.pickup && (
                      <span className="text-[9px] uppercase tracking-[0.18em] bg-secondary px-1.5 py-0.5 rounded-full">
                        Pickup
                      </span>
                    )}
                    {d.delivery && (
                      <span className="text-[9px] uppercase tracking-[0.18em] bg-secondary px-1.5 py-0.5 rounded-full">
                        Delivery
                      </span>
                    )}
                    {d.featured && (
                      <span
                        className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full"
                        style={{
                          color: "#D4A268",
                          background: "rgba(212,162,104,0.15)",
                        }}
                      >
                        Featured
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/dispensaries/${d.slug}`}
                    data-testid={`map-popup-visit-${d.slug}`}
                    className="inline-flex items-center text-xs text-primary hover:underline mt-2"
                  >
                    Visit menu <ArrowUpRight className="w-3 h-3 ml-1" />
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="container-narrow flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ background: "#7FA28A" }}
          />
          Dispensary
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ background: "#D4A268" }}
          />
          Featured
        </span>
        {location && (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ background: "#5CA9FF" }}
            />
            You · 10mi radius
          </span>
        )}
      </div>

      <LocationDialog open={locOpen} onOpenChange={setLocOpen} />
    </main>
  );
}
